import React, { useMemo } from 'react';
import { useImportExport, useMutationImport, useInfoHandler } from './useImportExport';
import {
  useWealthlaneAwardApi,
  useWealthlaneCustomerApi,
  useWealthlaneNotificationApi,
  useWealthlaneFinanceIntegrationApi,
  useWealthlaneReportApi,
} from '../index';
import useWealthlaneTaxApi from '../useWealthlaneTaxApi';
import usePostImportNotificationTemplate from '../post/usePostImportNotificationTemplate';
import usePostAwardAgreementDocument from '../post/usePostAwardAgreementDocument';
import usePostImportCustomReportJson from '../post/usePostImportCustomReportJson';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../pages/configuration/routeSlice';

// Services
import {
  ParticipantService,
  YtdService,
  CustomerTitleImportService,
} from '@wealthlane/api-services/wealthlane-customer-service';
import { RsuAwardService, PsuAwardService, OptionAwardService, EsppOfferingPeriodService, PerformanceRuleService, ImportOptionExerciseService, AwardAgreementDocumentImportService, FairValueService, VestingTrancheService, VestingTemplateService, AwardImportService, ExternalTransactionImportService, InboundShareTransferService, ImportTaDrsService, TaDrsInstructionService } from '@wealthlane/api-services/wealthlane-award-service';
import { TaxItemImportService } from '@wealthlane/api-services/wealthlane-tax-service';
import { PrivateTickerStockImportService } from '@wealthlane/api-services/wealthlane-finance-integration-service';
import { toast } from 'react-toastify';
import ToastComponent from '../../components/toast';
import { handleOpenFile } from '../../utils/global';
import { isEmpty } from 'lodash';

// Template Data Types
export interface TemplateOption {
  id?: string;
  title: string;
  download?: () => void;
  import?: (event: any) => void;
  acceptedFileTypes?: string;
}

export interface TemplateButton {
  id?: string;
  title: string;
  download?: () => void;
  import?: (event: any) => void;
  isDropdown?: boolean;
  options?: TemplateOption[];
  acceptedFileTypes?: string;
}

export interface TemplateItem {
  id: string;
  title: string;
  moduleKey?: string;
  permission?: string;
  download?: () => void;
  import?: (event: any) => void;
  buttons?: TemplateButton[];
  acceptedFileTypes?: string;
  importLabel?: string;
}

/**
 * Custom hook that provides all import/export functionality for the All-in-One page
 * Encapsulates all business logic and API calls
 */
export const useAllInOneImportExport = () => {
  const queryClient = useQueryClient();

  // API Handlers
  const { handleRequest: awardHandleRequest } = useWealthlaneAwardApi();
  const { handleRequest: customerHandleRequest } = useWealthlaneCustomerApi();
  const { handleRequest: taxHandleRequest } = useWealthlaneTaxApi();
  const { handleRequest: notificationHandleRequest } = useWealthlaneNotificationApi();
  const { handleRequest: financeHandleRequest } = useWealthlaneFinanceIntegrationApi();
  const { handleRequest: reportHandleRequest } = useWealthlaneReportApi();

  // Mutations
  const { mutate: importNotificationTemplate } = usePostImportNotificationTemplate(
    notificationHandleRequest,
    () => queryClient.invalidateQueries('getEmailTemplates')
  );

  const { mutate: importAwardAgreement } = usePostAwardAgreementDocument(awardHandleRequest, () =>
    toast.success(
      React.createElement(ToastComponent, {
        label: 'Success',
        message: 'Award Agreement imported successfully.',
      })
    )
  );

  const { mutate: importCustomReportJson } = usePostImportCustomReportJson(reportHandleRequest);

  // Participants handlers
  const participantHandlers = useImportExport({
    handleRequest: customerHandleRequest,
    config: {
      downloadService: () =>
        ParticipantService.postApiCustomerApiAppParticipantDownloadParticipantSample(),
      importService: (payload: any) =>
        ParticipantService.postApiCustomerApiAppParticipantImportBulkParticipants(payload),
      fileName: 'Participant Template.xlsx',
      successMessage: 'Participant import job submitted.',
    },
  });

  const ytdHandlers = useImportExport({
    handleRequest: customerHandleRequest,
    config: {
      downloadService: () => YtdService.postApiCustomerApiAppYtdDownloadYtdImportSample(),
      importService: (payload: any) => YtdService.postApiCustomerApiAppYtdImportYtd(payload),
      fileName: 'YTD Template.xlsx',
      successMessage: 'YTD import job submitted.',
      payloadKey: 'ImportFile',
    },
  });

  // RSU handlers
  const rsuHandlers = useImportExport({
    handleRequest: awardHandleRequest,
    config: {
      downloadService: () => RsuAwardService.postApiAwardApiAppRSUAwardDownloadRSuAwardSample(),
      importService: (payload: any) =>
        RsuAwardService.postApiAwardApiAppRSUAwardValidateBulkRSu(payload),
      fileName: 'RSU Award Template.xlsx',
      successMessage: 'RSU import job submitted.',
    },
  });

  // Option handlers
  const optionHandlers = useImportExport({
    handleRequest: awardHandleRequest,
    config: {
      downloadService: () =>
        OptionAwardService.postApiAwardApiAppOptionAwardDownloadOptionAwardSample(),
      importService: (payload: any) =>
        OptionAwardService.postApiAwardApiAppOptionAwardValidateBulkOptionAwards(payload),
      fileName: 'Option Award Template.xlsx',
      successMessage: 'Option awards import job submitted.',
    },
  });

  // PSU handlers
  const psuHandlers = useImportExport({
    handleRequest: awardHandleRequest,
    config: {
      downloadService: () => PsuAwardService.postApiAwardApiAppPSUAwardDownloadPSuAwardSample(),
      importService: (payload: any) =>
        PsuAwardService.postApiAwardApiAppPSUAwardValidateBulkPSu(payload),
      fileName: 'PSU Award Template.xlsx',
      successMessage: 'PSU import job submitted.',
    },
  });

  // ESPP handlers
  const esppHandlers = useImportExport({
    handleRequest: awardHandleRequest,
    config: {
      downloadService: () =>
        EsppOfferingPeriodService.getApiAwardApiAwardOfferingPeriodTemplateExport(),
      importService: (payload: any) =>
        EsppOfferingPeriodService.postApiAwardApiAwardOfferingPeriodImport(payload),
      fileName: 'ESPP Offering Period Template.xlsx',
      successMessage: 'ESPP Offering import job submitted.',
    },
  });

  // Job Title handlers
  const jobTitleHandlers = useImportExport({
    handleRequest: customerHandleRequest,
    config: {
      downloadService: () =>
        CustomerTitleImportService.postApiCustomerApiAppCustomerTitleImportDownloadCustomerTitleImportSample(),
      importService: (payload: any) =>
        CustomerTitleImportService.postApiCustomerApiAppCustomerTitleImportImportCustomerTitle(
          payload
        ),
      fileName: 'Job Title Template.xlsx',
      successMessage: 'Job Title import job submitted.',
      payloadKey: 'ImportFile',
    },
  });

  // Tax handlers
  const taxHandlers = useImportExport({
    handleRequest: taxHandleRequest,
    config: {
      downloadService: () =>
        TaxItemImportService.postApiTaxApiAppTaxItemImportDownloadTaxItemImportSample(),
      importService: (payload: any) =>
        TaxItemImportService.postApiTaxApiAppTaxItemImportImportTaxItem(payload),
      fileName: 'Tax Template.xlsx',
      successMessage: 'Tax import job submitted.',
      payloadKey: 'ImportFile',
    },
  });

  // Performance Rule handlers
  const performanceRuleHandlers = useImportExport({
    handleRequest: awardHandleRequest,
    config: {
      downloadService: () =>
        PerformanceRuleService.postApiAwardApiAppPerformanceRuleDownloadPerformanceRuleTemplate(),
      importService: (payload: any) =>
        PerformanceRuleService.postApiAwardApiAppPerformanceRuleImportPerformanceRule(payload),
      fileName: 'Performance Rule Template.xlsx',
      successMessage: 'Performance Rule import job submitted.',
    },
  });

  // Exercise handlers
  const exerciseHandlers = useImportExport({
    handleRequest: awardHandleRequest,
    config: {
      downloadService: () =>
        ImportOptionExerciseService.postApiAwardApiAppImportOptionExerciseDownloadTemplate(),
      importService: (payload: any) =>
        ImportOptionExerciseService.postApiAwardApiAppImportOptionExerciseOptionExerciseImportRequest(
          payload
        ),
      fileName: 'Early Exercise Template.xlsx',
      successMessage: 'Early Exercise import job submitted.',
    },
  });

  // Award Agreement handlers
  const awardAgreementHandlers = useImportExport({
    handleRequest: awardHandleRequest,
    config: {
      downloadService: () =>
        AwardAgreementDocumentImportService.postApiAwardApiAppAwardAgreementDocumentImportDownloadExternalTransactionImportSample(),
      fileName: 'Award Agreement Template.xlsx',
    },
  });

  // Fair Value handlers
  const fairValueHandlers = useImportExport({
    handleRequest: awardHandleRequest,
    config: {
      downloadService: () =>
        FairValueService.postApiAwardApiAppFairValueDownloadFairValueImportSample(),
      importService: (payload: any) =>
        FairValueService.postApiAwardApiAppFairValueImportFairValue(payload),
      fileName: 'Fair Value Template.xlsx',
      successMessage: 'Fair Value import job submitted.',
    },
  });

  // Vesting Tranches handlers
  const vestingTrancheHandlers = useImportExport({
    handleRequest: awardHandleRequest,
    config: {
      downloadService: () =>
        VestingTrancheService.postApiAwardApiAppVestingTrancheDownloadOptionAwardSample(),
      importService: (payload: any) =>
        VestingTrancheService.postApiAwardApiAppVestingTrancheImportVestingTranche(payload),
      fileName: 'Vesting Tranche Template.xlsx',
      successMessage: 'Vesting Tranches import job submitted.',
    },
  });

  // Unified Award handlers
  const unifiedAwardHandlers = useImportExport({
    handleRequest: awardHandleRequest,
    config: {
      downloadService: () => AwardImportService.postApiAwardApiAppAwardImportDownloadImportSample(),
      importService: (payload: any) =>
        AwardImportService.postApiAwardApiAppAwardImportAwardImport(payload),
      fileName: 'Unified Award Import Template.xlsx',
      successMessage: 'Unified Award import job submitted.',
    },
  });

  // External Transaction handlers
  const externalTransactionHandlers = useImportExport({
    handleRequest: awardHandleRequest,
    config: {
      downloadService: () =>
        ExternalTransactionImportService.postApiAwardApiAppExternalTransactionImportDownloadExternalTransactionImportSample(),
      importService: (payload: any) =>
        ExternalTransactionImportService.postApiAwardApiAppExternalTransactionImportImportExternalTransaction(
          payload
        ),
      fileName: 'External Transaction Template.xlsx',
      successMessage: 'External Transaction import job submitted.',
    },
  });

  // Inbound Share Transfer handlers
  const inboundShareTransferHandlers = useImportExport({
    handleRequest: awardHandleRequest,
    config: {
      downloadService: () =>
        InboundShareTransferService.postApiAwardApiAppInboundShareTransferDownloadInboundShareTransferTemplate(),
      importService: (payload: any) =>
        InboundShareTransferService.postApiAwardApiAppInboundShareTransferImportInboundShareTransfer(
          payload
        ),
      fileName: 'Inbound Share Transfer Template.xlsx',
      successMessage: 'Inbound Share Transfer import job submitted.',
    },
  });

  // Private Ticker Stock handlers
  const privateTickerHandlers = useImportExport({
    handleRequest: financeHandleRequest,
    config: {
      downloadService: () =>
        PrivateTickerStockImportService.postApiFinanceIntegrationApiAppPrivateTickerStockImportDownloadSample(),
      importService: (payload: any) =>
        PrivateTickerStockImportService.postApiFinanceIntegrationApiAppPrivateTickerStockImportImport(
          payload
        ),
      fileName: 'Private Stock Data Sample.xlsx',
      successMessage: 'Private ticker import job submitted.',
    },
  });

  // Vesting Template handlers
  const vestingTemplateHandlers = useImportExport({
    handleRequest: awardHandleRequest,
    config: {
      downloadService: () =>
        VestingTemplateService.postApiAwardApiAppVestingTemplateDownloadVestingTemplateSample(),
      importService: (payload: any) =>
        VestingTemplateService.postApiAwardApiAppVestingTemplateImportBulkVestingTemplateExcel(
          payload
        ),
      fileName: 'Vesting Template.xlsx',
      successMessage: 'Vesting Template import job submitted.',
    },
  });

  // Special handlers for mutation-based imports
  const importEmailTemplate = useMutationImport(importNotificationTemplate);
  const importAwardAgreementTemplate = useMutationImport(importAwardAgreement, 'file');
  const importCustomReportJsonTemplate = useMutationImport(
    importCustomReportJson,
    'file',
    '.wrc, .json'
  );

  // Info handlers for features requiring additional context
  const downloadGrantTemplate = useInfoHandler(
    'Grant template download requires plan selection from Plan Management page.'
  );
  const importGrantTemplate = useInfoHandler(
    'Grant template import requires plan selection from Plan Management page.'
  );

  // Special export handlers
  const exportInboundShareTransfer = () => {
    awardHandleRequest(
      InboundShareTransferService.postApiAwardApiAppInboundShareTransferExportInboundShare()
    )
      .then((data: any) => {
        if (data) {
          handleOpenFile(`${data?.name || 'Inbound Share Transfer Export'}.xlsx`, data.content);
        }
      })
      .catch(() => { });
  };

  const exportTransferAgentDRS = () => {
    const payload = { transferStatus: null };
    awardHandleRequest(
      TaDrsInstructionService.postApiAwardApiAppTaDrsInstructionExportTAdrsInstruction(payload)
    )
      .then((response: any) => {
        if (response) {
          toast.success(
            React.createElement(ToastComponent, {
              label: 'Success',
              message: 'DRS Instruction Exported',
            })
          );
          handleOpenFile('DRS Instruction.xlsx', response);
        }
      })
      .catch(() => { });
  };

  const importTransferAgentDRS = (event: any) => {
    if (!isEmpty(event.target.files)) {
      const payload = { File: event.target.files[0] };
      awardHandleRequest(
        ImportTaDrsService.postApiAwardApiAppImportTaDrsImportDrsInstruction(payload)
      )
        .then((response: any) => {
          if (response) {
            toast.success(
              React.createElement(ToastComponent, {
                label: 'Success',
                message: 'DRS Instruction import job submitted.',
              })
            );
          }
        })
        .catch(() => { })
        .finally(() => {
          event.target.value = null;
        });
    }
  };

  // Template data configuration
  const templateDatas: TemplateItem[] = useMemo(
    () => [
      {
        id: 'awardAgreement',
        title: 'Award Agreement',
        moduleKey: 'award-agreement',
        permission: 'CustomerService.Customer.RSUAwards',
        download: awardAgreementHandlers.downloadTemplate,
        import: importAwardAgreementTemplate,
      },
      // {
      //   id: 'customReportJson',
      //   title: '',
      //   moduleKey: 'reports',
      //   permission: 'CustomerService.Customer.DynamicReport',
      //   import: importCustomReportJsonTemplate,
      //   acceptedFileTypes: '.wrc, .json',
      //   importLabel: 'Import Configuration',
      // },
      {
        id: 'emailTemplate',
        title: 'Email Template',
        moduleKey: 'email-template',
        permission: 'CustomerService.Customer.NotificationTemplate',
        import: importEmailTemplate,
      },
      {
        id: 'espp',
        title: 'ESPP',
        moduleKey: 'espp',
        permission: 'CustomerService.Customer.OfferingPeriod',
        download: esppHandlers.downloadTemplate,
        import: esppHandlers.importData,
      },
      {
        id: 'exercise',
        title: 'Exercise',
        moduleKey: 'exercise',
        permission: 'CustomerService.Customer.OptionExercise',
        download: exerciseHandlers.downloadTemplate,
        import: exerciseHandlers.importData,
      },
      {
        id: 'externalTransaction',
        title: 'External Transaction',
        moduleKey: 'transactions',
        permission: 'CustomerService.Customer.TransferAllocate',
        download: externalTransactionHandlers.downloadTemplate,
        import: externalTransactionHandlers.importData,
      },
      {
        id: 'fairValue',
        title: 'Fair Value',
        moduleKey: 'fair-value',
        permission: 'CustomerService.Customer.RSUAwards',
        download: fairValueHandlers.downloadTemplate,
        import: fairValueHandlers.importData,
      },
      {
        id: 'grantTemplate',
        title: 'Grant Template',
        moduleKey: 'grant-acceptance-template',
        permission: 'CustomerService.Customer.Plans',
        download: downloadGrantTemplate,
        import: importGrantTemplate,
      },
      {
        id: 'inboundShareTransfer',
        title: 'Inbound Share Transfer',
        moduleKey: 'transactions',
        permission: 'CustomerService.Customer.InboundShareTransfer',
        download: inboundShareTransferHandlers.downloadTemplate,
        import: inboundShareTransferHandlers.importData,
      },
      {
        id: 'jobTitle',
        title: 'Job Title',
        moduleKey: 'job-title',
        permission: 'CustomerService.Customer.CustomerTitles',
        download: jobTitleHandlers.downloadTemplate,
        import: jobTitleHandlers.importData,
      },
      {
        id: 'option',
        title: 'Option Awards',
        moduleKey: 'option-awards',
        permission: 'CustomerService.Customer.OptionAwards',
        download: optionHandlers.downloadTemplate,
        import: optionHandlers.importData,
      },
      {
        id: 'participants',
        title: 'Participants',
        moduleKey: 'participants',
        permission: 'CustomerService.Customer.Participants',
        buttons: [
          {
            id: 'downloadTemplate',
            title: 'Download Template',
            isDropdown: true,
            options: [
              {
                id: 'downloadParticipantTemplate',
                title: 'Download Participant Template',
                download: participantHandlers.downloadTemplate,
              },
              {
                id: 'downloadYtdTemplate',
                title: 'Download YTD Template',
                download: ytdHandlers.downloadTemplate,
              },
            ],
          },
          {
            id: 'importTemplate',
            title: 'Import Template',
            isDropdown: true,
            options: [
              {
                id: 'importBulkParticipantXls',
                title: 'Import Bulk Participant XLS',
                import: participantHandlers.importData,
              },
              {
                id: 'importBulkYtdXls',
                title: 'Import Bulk YTD XLS',
                import: ytdHandlers.importData,
              },
            ],
          },
        ],
      },
      {
        id: 'performanceRule',
        title: 'Performance Rule',
        moduleKey: 'performance-rule',
        permission: 'CustomerService.Customer.PerformanceRule',
        download: performanceRuleHandlers.downloadTemplate,
        import: performanceRuleHandlers.importData,
      },
      {
        id: 'privateTickerStock',
        title: 'Private Ticker Stock',
        moduleKey: 'historical-stock-price',
        permission: 'CustomerService.Customer.PrivateCompanyStock',
        download: privateTickerHandlers.downloadTemplate,
        import: privateTickerHandlers.importData,
      },
      {
        id: 'psu',
        title: 'PSU Awards',
        moduleKey: 'psu-awards',
        permission: 'CustomerService.Customer.PSUAwards',
        download: psuHandlers.downloadTemplate,
        import: psuHandlers.importData,
      },
      {
        id: 'rsu',
        title: 'RSU Awards',
        moduleKey: 'rsu-awards',
        permission: 'CustomerService.Customer.RSUAwards',
        download: rsuHandlers.downloadTemplate,
        import: rsuHandlers.importData,
      },
      {
        id: 'tax',
        title: 'Tax Setup',
        moduleKey: 'tax',
        permission: 'CustomerService.Customer.CompanyTax',
        download: taxHandlers.downloadTemplate,
        import: taxHandlers.importData,
      },
      {
        id: 'transferAgentDRS',
        title: 'Transfer Agent DRS',
        moduleKey: 'transfer-agent-import-export',
        permission: 'CustomerService.Customer.DrsInstruction',
        download: exportTransferAgentDRS,
        import: importTransferAgentDRS,
      },
      {
        id: 'unifiedAwardImport',
        title: 'Unified Award Import',
        moduleKey: 'unified-award-import',
        permission: 'CustomerService.Customer.RSUAwards',
        download: unifiedAwardHandlers.downloadTemplate,
        import: unifiedAwardHandlers.importData,
      },
      {
        id: 'vestingTemplate',
        title: 'Vesting Template',
        moduleKey: 'vesting-templates',
        permission: 'CustomerService.Customer.VestingTemplates',
        download: vestingTemplateHandlers.downloadTemplate,
        import: vestingTemplateHandlers.importData,
      },
      {
        id: 'vestingTranches',
        title: 'Vesting Tranches',
        moduleKey: 'vesting-tranches',
        permission: 'CustomerService.Customer.RSUAwards',
        download: vestingTrancheHandlers.downloadTemplate,
        import: vestingTrancheHandlers.importData,
      },
    ],
    [
      participantHandlers,
      ytdHandlers,
      rsuHandlers,
      optionHandlers,
      psuHandlers,
      esppHandlers,
      jobTitleHandlers,
      taxHandlers,
      performanceRuleHandlers,
      exerciseHandlers,
      awardAgreementHandlers,
      fairValueHandlers,
      vestingTrancheHandlers,
      unifiedAwardHandlers,
      externalTransactionHandlers,
      inboundShareTransferHandlers,
      privateTickerHandlers,
      vestingTemplateHandlers,
      importEmailTemplate,
      importAwardAgreementTemplate,
      importCustomReportJsonTemplate,
      downloadGrantTemplate,
      importGrantTemplate,
    ]
  );

  // Get user permissions from Redux
  const userAuthList = useSelector(selectAuthRoutesList);
  const userPermissions = userAuthList?.auth?.grantedPolicies || {};

  // Filter templateDatas based on user permissions
  const filteredTemplateDatas = useMemo(() => {
    return templateDatas.filter((item) => {
      // If no permission is specified, show the item
      if (!item.permission) return true;

      // Check if user has the required permission
      return Boolean(userPermissions[item.permission]);
    });
  }, [templateDatas, userPermissions]);

  console.log('templateDatas', templateDatas.length, 'filtered', filteredTemplateDatas.length);

  return {
    templateDatas: filteredTemplateDatas,
  };
};
